import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Params, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Awards, db_GetAwardRow, db_ListQuestionsBySurveyIDRow } from '../../../api';
import { SurveyDropdown } from '../../../components/dropdowns/SurveyDropdown';
import Breadcrumbs, { BreadcrumbItem } from '../../../components/govuk/Breadcrumbs';
import Button from '../../../components/govuk/Button';
import ErrorSummary from '../../../components/govuk/ErrorSummary';
import Fieldset, { Legend } from '../../../components/govuk/Fieldset';
import { GridColumn, GridRow } from '../../../components/govuk/Grid';
import Table, { TD, TR } from '../../../components/govuk/Table';
import Tag from '../../../components/govuk/Tag';
import { H3 } from '../../../components/govuk/Typography';
import Markdown from '../../../components/Markdown';
import LoadingPage from '../../LoadingPage';
import { getSurveyQuestionsBySurveyIDQuery } from '../../Survey/SurveyResponseViewPage';
import { getAwardQuery } from '../awardQuery';

interface AwardSurveyProps {
  fieldName: string;
  label: string;
  surveyType: string;
}

interface AwardSurveyFormProps extends AwardSurveyProps {
  awardData: db_GetAwardRow;
}

function questionTypeLabel(questionType?: string): string {
  switch (questionType) {
    case 'text':
      return 'Free text';
    case 'boolean':
      return 'Yes / No';
    case 'radio':
      return 'Multiple choice';
    case 'scale':
      return 'Scale';
    default:
      return questionType || 'Unknown';
  }
}

function SurveyQuestionPreviewItem({ question }: { question: db_ListQuestionsBySurveyIDRow }) {
  return (
    <div className="govuk-!-margin-bottom-6">
      <H3>
        <Markdown>{question.question_text}</Markdown>
      </H3>

      {question.hint && <Markdown>{question.hint}</Markdown>}

      <Table>
        <TR>
          <TD>Order</TD>
          <TD>{question.display_order}</TD>
        </TR>
        <TR>
          <TD>Type</TD>
          <TD>{questionTypeLabel(question.question_type)}</TD>
        </TR>
        <TR>
          <TD>Required</TD>
          <TD>
            <Tag color={question.is_required ? 'red' : 'grey'}>{question.is_required ? 'Required' : 'Optional'}</Tag>
          </TD>
        </TR>
        {question.question_type === 'text' && (!!question.min_word_count || !!question.max_word_count) && (
          <TR>
            <TD>Word count</TD>
            <TD>{`${question.min_word_count || 0} to ${question.max_word_count || 0} words`}</TD>
          </TR>
        )}
        {!!question.answer_options?.length && (
          <TR>
            <TD>Answer options</TD>
            <TD>{question.answer_options.join(', ')}</TD>
          </TR>
        )}
        {!!question.correct_answer?.length && (
          <TR>
            <TD>Correct answer</TD>
            <TD>{question.correct_answer.join(', ')}</TD>
          </TR>
        )}
      </Table>
    </div>
  );
}

function SurveyQuestionsPreview({ surveyID }: { surveyID: number }) {
  const { data: questions, isLoading } = useQuery(getSurveyQuestionsBySurveyIDQuery(surveyID));

  if (isLoading) return <LoadingPage />;
  if (!questions?.length) return null;

  const orderedQuestions = [...questions].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  return (
    <>
      <H3>Survey preview</H3>
      {orderedQuestions.map((question) => (
        <SurveyQuestionPreviewItem key={question.question_id} question={question} />
      ))}
    </>
  );
}

function AwardSurveyForm({ awardData, fieldName, label, surveyType }: AwardSurveyFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const awardID = awardData.award_id ?? 0;
  const surveyID = awardData?.[fieldName as keyof typeof awardData];

  const FormSchema = z.object({
    survey_id: z.coerce.number().min(1, { message: 'Select a survey' }),
  });
  type FormData = z.infer<typeof FormSchema>;

  const {
    formState: { errors },
    handleSubmit,
    register,
    setFocus,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      survey_id: surveyID,
    },
    resolver: zodResolver(FormSchema) as never,
    reValidateMode: 'onSubmit',
  });

  const selectedSurveyID = Number(watch('survey_id')) || 0;

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      await Awards.updateAward(awardID, {
        [fieldName as keyof db_GetAwardRow]: data.survey_id,
      });
      queryClient.invalidateQueries({ queryKey: ['awards'] });
      navigate(`/awards/${awardID}/view`);
    } catch {
      setSubmitError(`Error updating ${label.toLowerCase()}.`);
    }

    setIsSubmitting(false);
  };

  const validationErrors = Object.keys(errors);

  return (
    <GridRow>
      <GridColumn width="three-quarters">
        <Breadcrumbs>
          <BreadcrumbItem to="/dashboard">Dashboard</BreadcrumbItem>
          <BreadcrumbItem to={`/awards/${awardID}/view`}>{awardData?.award_name}</BreadcrumbItem>
          <BreadcrumbItem>Change {label.toLowerCase()}</BreadcrumbItem>
        </Breadcrumbs>

        {submitError && !validationErrors.length && <ErrorSummary description={submitError} />}

        <form onSubmit={handleSubmit(onSubmit)}>
          {!!validationErrors?.length && (
            <ErrorSummary
              errors={validationErrors.map((key) => ({
                targetName: key,
                text: errors[key as keyof typeof errors]?.message,
              }))}
              onHandleErrorClick={(targetName: string) => setFocus(targetName as keyof FormData)}
            />
          )}

          <Fieldset>
            <Legend isPageHeading size="l">
              Change the {label.toLowerCase()} for {awardData?.award_name}
            </Legend>

            <SurveyDropdown
              label="Award eligibility survey"
              columnFilters={[
                { id: 'survey_type', value: surveyType },
                { id: 'is_active', value: 'true' },
              ]}
              error={errors?.survey_id?.message}
              {...register('survey_id')}
            />

            <Button type="submit" disabled={isSubmitting}>
              Continue
            </Button>
          </Fieldset>
        </form>

        {selectedSurveyID > 0 && <SurveyQuestionsPreview surveyID={selectedSurveyID} />}
      </GridColumn>
    </GridRow>
  );
}

export default function AwardSurveyPage(props: AwardSurveyProps) {
  const params = useParams<Params>();
  const awardID = parseInt(params.id ?? '0');

  const { data: awardData, isLoading: awardIsLoading } = useQuery(getAwardQuery(awardID));

  if (awardIsLoading) return <LoadingPage />;
  if (awardData) return <AwardSurveyForm awardData={awardData} {...props} />;
}
